import type { ConsentAction, ConsentChoice } from '@cmp/shared';

export const COOKIE_NAME = 'cmp_consent';
const LS_KEY = 'cmp_consent';

export interface StoredConsent {
  /** Anonymous consent id. Also the id of the log row on the edge. */
  id: string;
  /** Config version the choice was made against. */
  v: string;
  /** Unix ms. */
  ts: number;
  a: ConsentAction;
  c: ConsentChoice;
}

export function randomId(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += bytes[i]!.toString(16).padStart(2, '0');
  return s;
}

function encode(v: StoredConsent): string {
  return encodeURIComponent(JSON.stringify(v));
}

function decode(raw: string | null | undefined): StoredConsent | null {
  if (!raw) return null;
  try {
    const o = JSON.parse(decodeURIComponent(raw)) as Partial<StoredConsent>;
    if (!o || typeof o.id !== 'string' || typeof o.v !== 'string' || typeof o.ts !== 'number' || !o.c) return null;
    return o as StoredConsent;
  } catch {
    return null;
  }
}

function readCookie(name: string): string | null {
  const parts = document.cookie.split('; ');
  for (const p of parts) {
    const i = p.indexOf('=');
    if (i > 0 && p.slice(0, i) === name) return p.slice(i + 1);
  }
  return null;
}

/** Pick the widest domain we are allowed to set a cookie on so subdomains share consent. */
export function cookieDomain(configDomain: string | undefined, host: string): string | undefined {
  if (!configDomain) return undefined;
  const d = configDomain.replace(/^\./, '').toLowerCase();
  const h = host.toLowerCase();
  if (h === d || h.endsWith('.' + d)) return d;
  return undefined;
}

export function readState(): StoredConsent | null {
  const fromCookie = decode(readCookie(COOKIE_NAME));
  if (fromCookie) return fromCookie;
  try {
    return decode(localStorage.getItem(LS_KEY));
  } catch {
    return null;
  }
}

export function writeState(state: StoredConsent, days: number, domain?: string): void {
  const value = encode(state);
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  let cookie = `${COOKIE_NAME}=${value}; Expires=${expires}; Path=/; SameSite=Lax`;
  if (domain) cookie += `; Domain=${domain}`;
  if (location.protocol === 'https:') cookie += '; Secure';
  document.cookie = cookie;
  try {
    localStorage.setItem(LS_KEY, value);
  } catch {
    /* storage may be blocked; the cookie is the source of truth */
  }
}

export function clearState(domain?: string): void {
  const base = `${COOKIE_NAME}=; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Path=/; SameSite=Lax`;
  document.cookie = base;
  if (domain) document.cookie = `${base}; Domain=${domain}`;
  try {
    localStorage.removeItem(LS_KEY);
  } catch {
    /* ignore */
  }
}

/** A stored choice is usable if it matches the config version and has not expired. */
export function isValid(state: StoredConsent | null, version: string, reconsentDays: number, refusalDays: number): state is StoredConsent {
  if (!state) return false;
  if (state.v !== version) return false;
  const anyOptional = state.c.analytics || state.c.marketing || state.c.functionality;
  const ttlDays = anyOptional ? reconsentDays : refusalDays;
  return Date.now() - state.ts < ttlDays * 864e5;
}
